import { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Typography,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Group as GroupIcon,
  Domain as DomainIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useQuery } from "react-query";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";

function TenantSelection({
  userIdentifier,
  identifierType,
  onBack,
  onTenantSelected,
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { switchToTenant } = useContext(AuthContext);
  const theme = useTheme();

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [hoveredTenant, setHoveredTenant] = useState(null);

  // Build query params for the API call
  const queryParams = {
    [identifierType]: userIdentifier,
  };

  // Fetch user tenants
  const {
    data: tenantsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["user-tenants", userIdentifier, identifierType],
    () => axios.get("/users/tenants", { params: queryParams }),
    {
      enabled: !!userIdentifier,
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const tenants = tenantsResponse?.data?.data || [];

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setConfirmDialogOpen(true);
  };

  const handleConfirmSelection = async () => {
    if (!selectedTenant) return;

    try {
      // Switch to the selected tenant context
      const result = await switchToTenant(selectedTenant.id, false);

      if (result.success) {
        // Notify parent component about tenant selection
        if (onTenantSelected) {
          onTenantSelected(selectedTenant);
        }

        enqueueSnackbar(`Successfully switched to ${selectedTenant.name}`, {
          variant: "success",
          autoHideDuration: 2000,
        });
      }
    } catch (error) {
      console.error("Error switching tenant:", error);
      enqueueSnackbar("Failed to switch tenant. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setSelectedTenant(null);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleRetry = () => {
    refetch();
  };

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load tenants";

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 4000,
      });
    }
  }, [error, enqueueSnackbar]);

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            mx: { xs: 1, sm: 0 },
          }}
        >
          {/* Header Skeleton */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <Skeleton
              variant="rectangular"
              width={{ xs: 45, sm: 60 }}
              height={{ xs: 35, sm: 45 }}
              sx={{ mr: 2, borderRadius: 1 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="text"
                width="40%"
                height={{ xs: 30, sm: 40 }}
              />
              <Skeleton variant="text" width="60%" />
            </Box>
          </Box>

          {/* Loading skeleton for tenant cards */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            Loading your available tenants...
          </Typography>

          {[1, 2, 3].map((item) => (
            <Card
              key={item}
              sx={{
                mb: 2,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Skeleton
                  variant="circular"
                  width={{ xs: 50, sm: 60 }}
                  height={{ xs: 50, sm: 60 }}
                  sx={{ mr: { xs: 2, sm: 3 } }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={{ xs: 24, sm: 28 }}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={{ xs: 18, sm: 20 }}
                  />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={16}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            background: "linear-gradient(145deg, #fef2f2 0%, #ffffff 100%)",
            border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
            mx: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
                mb: { xs: 1, sm: 0 },
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Back
              </Button>
              <Avatar
                sx={{
                  width: { xs: 45, sm: 50, md: 60 },
                  height: { xs: 35, sm: 38, md: 45 },
                  borderRadius: 0,
                  mr: { xs: 0, sm: 2 },
                  order: { xs: -1, sm: 0 },
                }}
                src={logo}
                alt="Logo"
              />
            </Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Select Tenant
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", py: 4 }}>
            <ErrorIcon
              sx={{
                fontSize: { xs: 48, sm: 64 },
                color: "error.main",
                mb: 2,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="h6"
              color="error"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              Unable to Load Tenants
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: 400,
                mx: "auto",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              We encountered an issue while loading your available tenants. This
              might be due to network issues or server problems.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", sm: "flex-end" },
                flexWrap: "wrap",
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                onClick={handleRetry}
                startIcon={<SecurityIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: "primary.main",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // No tenants found
  if (tenants.length === 0 && !isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            background: "linear-gradient(145deg, #f0f9ff 0%, #ffffff 100%)",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            mx: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: 2, sm: 0 },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
                mb: { xs: 1, sm: 0 },
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Back
              </Button>
              <Avatar
                sx={{
                  width: { xs: 45, sm: 50, md: 60 },
                  height: { xs: 35, sm: 38, md: 45 },
                  borderRadius: 0,
                  mr: { xs: 0, sm: 2 },
                  order: { xs: -1, sm: 0 },
                }}
                src={logo}
                alt="Logo"
              />
            </Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Select Tenant
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", py: 4 }}>
            <DomainIcon
              sx={{
                fontSize: { xs: 48, sm: 64 },
                color: "primary.main",
                mb: 2,
                opacity: 0.8,
              }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              No Tenants Available
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 3,
                maxWidth: 400,
                mx: "auto",
                lineHeight: 1.6,
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              No tenants are currently associated with this {identifierType}.
              You can register a new tenant or contact your administrator for
              access.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: { xs: "center", sm: "flex-end" },
                flexWrap: "wrap",
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/tenant-registration")}
                startIcon={<BusinessIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: "primary.main",
                  boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                Register New Tenant
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 1, sm: 3, md: 4 } }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: 2, md: 3 },
          background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mx: { xs: 1, sm: 0 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: { xs: 3, sm: 4 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "space-between", sm: "flex-start" },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                mr: { xs: 0, sm: 2 },
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                minWidth: "auto",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Back
            </Button>
            <Avatar
              sx={{
                width: { xs: 45, sm: 50, md: 60 },
                height: { xs: 35, sm: 38, md: 45 },
                borderRadius: 0,
                mr: { xs: 0, sm: 2 },
                order: { xs: -1, sm: 0 },
              }}
              src={logo}
              alt="Logo"
            />
          </Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              background: "primary.main",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Select Tenant
          </Typography>
        </Box>

        {/* Introduction Text */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: { xs: 2, sm: 3 },
            textAlign: { xs: "left", sm: "center" },
            lineHeight: 1.6,
            fontSize: { xs: "0.9rem", sm: "1rem" },
          }}
        >
          Choose the workspace you want to access. You have access to{" "}
          {tenants.length} tenant{tenants.length !== 1 ? "s" : ""}.
        </Typography>

        {/* Tenant List */}
        <List sx={{ p: 0, mb: { xs: 2, sm: 3 } }}>
          {tenants.map((tenant, index) => (
            <Card
              key={tenant?.tenant?.id}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                borderRadius: { xs: 1.5, sm: 2 },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform:
                  hoveredTenant === tenant?.tenant?.id
                    ? { xs: "none", sm: "translateY(-2px)" }
                    : "none",
                boxShadow:
                  hoveredTenant === tenant?.tenant?.id ? { xs: 2, sm: 4 } : 1,
                border: `1px solid ${alpha(
                  theme.palette.primary.main,
                  hoveredTenant === tenant?.tenant?.id ? 0.2 : 0.1
                )}`,
                overflow: "hidden",
                position: "relative",
                "&::before":
                  hoveredTenant === tenant?.tenant?.id
                    ? {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background:
                          "linear-gradient(145deg, #f5f7fa 0%, #ffffff 100%)",
                      }
                    : {},
              }}
              onMouseEnter={() => setHoveredTenant(tenant?.tenant?.id)}
              onMouseLeave={() => setHoveredTenant(null)}
            >
              <ListItemButton
                onClick={() => handleTenantSelect(tenant?.tenant)}
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={tenant?.tenant?.logo}
                    sx={{
                      bgcolor: "primary.main",
                      width: { xs: 50, sm: 60 },
                      height: { xs: 50, sm: 60 },
                      mr: { xs: 2, sm: 2 },
                      boxShadow: 2,
                      transition: "all 0.3s ease",
                      transform:
                        hoveredTenant === tenant?.tenant?.id
                          ? { xs: "none", sm: "scale(1.1)" }
                          : "scale(1)",
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                          mr: 1,
                        }}
                      >
                        {tenant?.tenant?.name}
                      </Typography>
                      {index === 0 && (
                        <Chip
                          label="Recent"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            "& .MuiChip-label": {
                              px: { xs: 0.5, sm: 1 },
                            },
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 0.5,
                          display: "flex",
                          alignItems: "center",
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        }}
                      >
                        <GroupIcon
                          sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5 }}
                        />
                        ID: {tenant?.tenant?.id}
                      </Typography>
                      {tenant?.tenant?.origins &&
                        tenant?.tenant?.origins.length > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.75rem", sm: "0.8rem" },
                            }}
                          >
                            {tenant?.tenant?.origins.join(", ")}
                          </Typography>
                        )}
                    </Box>
                  }
                />
                <Box
                  sx={{
                    opacity: hoveredTenant === tenant?.tenant?.id ? 1 : 0,
                    transition: "opacity 0.3s ease",
                    color: "primary.main",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  <CheckCircleIcon />
                </Box>
              </ListItemButton>
            </Card>
          ))}
        </List>

        {/* Additional Actions */}
        <Box
          sx={{
            textAlign: "center",
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: 1.5, sm: 2 },
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Need access to another workspace?
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/tenant-registration")}
            startIcon={<BusinessIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              px: { xs: 2, sm: 3 },
            }}
          >
            Register New Tenant
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)",
            m: { xs: 2, sm: 3 },
            width: { xs: "calc(100% - 32px)", sm: "auto" },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            textAlign: "center",
            background: "linear-gradient(145deg, #f5f7fa 0%, #ffffff 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            p: { xs: 2, sm: 3 },
          }}
        >
          Confirm Tenant Selection
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
            {selectedTenant?.logo && (
              <Avatar
                src={selectedTenant.logo}
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  mx: "auto",
                  mb: 2,
                  boxShadow: 2,
                }}
              >
                <BusinessIcon />
              </Avatar>
            )}
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              {selectedTenant?.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              You are about to access the{" "}
              <strong>{selectedTenant?.name}</strong> workspace. Continue to
              proceed to the tenant dashboard.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            gap: 1,
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: { xs: "center", sm: "flex-end" },
          }}
        >
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              flex: 1,
              width: { xs: "100%", sm: "auto" },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSelection}
            variant="contained"
            autoFocus
            startIcon={<CheckCircleIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              flex: 1,
              width: { xs: "100%", sm: "auto" },
              background: "primary.main",
              boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Continue to Workspace
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TenantSelection;
